<script lang="ts">
    import { onDestroy, onMount, tick } from 'svelte'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'
    import { toUnicode } from 'punycode-esm'

    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import Date from 'hydria/components/Date.svelte'
    import MiniKeyboard from 'wdom/components/MiniKeyboard.svelte'

    const LIMIT = 10
    const DELAY_MS = 250

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        skip: 'int1+',
        status: 'string',
    }
    const mappingOrder = ['q', 'skip']

    // Search options for the backend
    interface SOptions {
        q: string
        skip: number
        status: string
    }
    const defaults: SOptions = {
        q: '',
        skip: 0,
        status: '',
    }

    // Search result from the server
    interface ISearchResult {
        items: Array<any>
        total_items: number
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/zone-ssl' : `/zone-ssl?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/zone-ssl' : `/zone-ssl?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    async function loadInitialData() {
        try {
            const r = await simpleFetch('GET', '/json/zone/1/ssl-status')

            const items: any[] = []
            for (const item of r.data.items) {
                item.idnaUnicode = toUnicode(item.idna)
                item.status = item.zone_entry == null ? 'ssl-only' : item.ssl_cert == null ? 'no-ssl' : 'ok'
                if (item.ssl_cert) {
                    for (const key of ['expiry', 'refreshed', 'ctime']) {
                        if (item.ssl_cert[key]) item.ssl_cert[key] = toLuxon(item.ssl_cert[key])
                    }
                }
                items.push(item)
            }
            allResults.set(items)
            extraReady = true
        } catch {
            notify({
                type: 'danger',
                title: 'SSL Certificates',
                text: 'La liste de certificats n’a pas pu être chargée.',
            })
        }
    }

    let extraReady = $state(false)

    $effect(() => {
        if (extraReady)
            tick().then(() => {
                unsubs.push(
                    querystring.subscribe(($qs) => {
                        loadQueryString($qs ? $qs : '')
                    }),
                )
            })
    })

    loadInitialData()

    let results = writable([] as any[])
    let allResults = writable([] as any[])

    let updating = $state(false)
    let loading = $state(true)
    let openAction: any | undefined = $state(undefined)
    // This is the non-proxied version of openActino
    let actionItem: any | undefined

    function doSearch() {
        updating = true
        if (openAction) {
            openAction = undefined
            actionItem = undefined
        }

        results.set(
            $allResults.filter((item) => {
                if (sOptions.status && item.status != sOptions.status) return false
                if (sOptions.q && !item.idna.match(sOptions.q) && !item.idnaUnicode.match(sOptions.q)) return false
                return true
            }),
        )
        updating = false
        if (loading) loading = false
    }

    // svelte-ignore state_referenced_locally
    let text = writable(sOptions.q)

    function loadIntoForm() {
        $text = sOptions.q
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    function onTimeout() {
        if ($text != sOptions.q) sOptions.q = $text
        textTimer = null
        setQueryString(sOptions)
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.q) {
                return
            }
            textTimer = setTimeout(onTimeout, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })

    let working = $state(false)

    async function confirmCreate() {
        working = true
        try {
            const r = await simpleFetch('PUT', `/json/zone/1/ssl/${actionItem.idna}`)
            notify({
                type: 'success',
                title: 'SSL Certificate',
                text: 'Le certificat a été créé.',
            })
            actionItem.ssl_cert = {}
            for (const key of ['expiry', 'refreshed', 'ctime']) {
                actionItem.ssl_cert[key] = toLuxon(r.data.cert[key])
            }
            actionItem.status = actionItem.zone_entry == null ? 'ssl-only' : 'ok'
            actionItem = undefined
            openAction = undefined

            allResults.update((v) => v)
            results.update((v) => v)
        } catch (e) {
            console.error('Error creating SSL:', e)
            notify({
                type: 'danger',
                title: 'SSL Certificate',
                text: 'Le certificat n’a pas pu être créé',
            })
        }
        working = false
    }

    async function confirmRevoke() {
        working = true
        try {
            await simpleFetch('DELETE', `/json/zone/1/ssl/${actionItem.idna}`)
            notify({
                type: 'success',
                title: 'SSL Certificate',
                text: 'Le certificat a été révoké.',
            })
            actionItem.ssl_cert = null
            actionItem.status = 'no-ssl'
            actionItem = undefined
            openAction = undefined
            allResults.update((v) => v)
            results.update((v) => v)
        } catch (e) {
            console.error('Error revoking SSL:', e)
            notify({
                type: 'danger',
                title: 'SSL Certificate',
                text: 'Le certificat n’a pas pu être révoké',
            })
        }
        working = false
    }
</script>

<style>
    td {
        vertical-align: middle;
    }
    td:nth-child(1),
    th:nth-child(1) {
        width: auto;
    }
    td:nth-child(2),
    th:nth-child(2) {
        width: auto;
    }
    td:nth-child(3),
    th:nth-child(3) {
        width: 12rem;
    }
    td:nth-child(4),
    th:nth-child(4) {
        width: 11rem;
    }
    td:nth-child(5),
    th:nth-child(5) {
        width: 11rem;
    }
    /* But: annuler couvre au moins l'espace de l'action */
    td:nth-child(4) button,
    td:nth-child(5) button {
        min-width: 4.75rem;
    }
</style>

<Breadcrumb crumbs={[{ txt: tgettext('Zones') }]} />

<div class="card mb-4">
    <div class="card-title text-primary">{$_('Filter parameters')}</div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
        >
            <div class="row">
                <div class="col-sm-4">
                    <div class="input-group">
                        <input type="text" class="form-control" id="idna-text" placeholder={$_('Zone name (regular expression)')} bind:this={mainInput} bind:value={$text} />
                        {#if $text}
                            <button
                                class="btn btn-secondary"
                                type="button"
                                disabled={updating}
                                onclick={() => {
                                    $text = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                        <MiniKeyboard eid={'idna-text'} />
                        <button class="btn btn-primary" type="submit"><Icon inline icon="fa:search" /></button>
                    </div>
                </div>
                <div class="col-sm-8 margin-top-mobile text-end">
                    <div class="btn-group">
                        <button
                            class="btn btn-outline-secondary"
                            type="button"
                            disabled={updating}
                            class:active={sOptions.status == ''}
                            onclick={() => {
                                sOptions.status = ''
                                setQueryString(sOptions)
                            }}>All</button
                        >
                        <button
                            class="btn btn-outline-secondary"
                            type="button"
                            disabled={updating}
                            class:active={sOptions.status == 'ok'}
                            onclick={() => {
                                sOptions.status = 'ok'
                                setQueryString(sOptions)
                            }}>OK</button
                        >
                        <button
                            class="btn btn-outline-secondary"
                            type="button"
                            disabled={updating}
                            class:active={sOptions.status == 'no-ssl'}
                            onclick={() => {
                                sOptions.status = 'no-ssl'
                                setQueryString(sOptions)
                            }}>Sans cert. SSL</button
                        >
                        <button
                            class="btn btn-outline-secondary"
                            type="button"
                            disabled={updating}
                            class:active={sOptions.status == 'ssl-only'}
                            onclick={() => {
                                sOptions.status = 'ssl-only'
                                setQueryString(sOptions)
                            }}>Sans entrée DNS</button
                        >
                    </div>
                </div>
            </div>
        </form>
    </div>
</div>

{#if loading}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card">
        <div class="card-title text-primary">{$_('Entries & SSL')} ({$results.length} / {$allResults.length})</div>

        {#if $results.length}
            <div class="card-body">
                <table class="table table-hover mb-0" class:disabled-look={updating} id="ssl-list">
                    <thead>
                        <tr>
                            <th>{$_('Name')}</th>
                            <th>{$_('Zone')}</th>
                            <th>{$_('SSL expiration')}</th>
                            <th>{$_('Create SSL')}</th>
                            <th>{$_('Remove SSL')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $results as item (item.idna)}
                            <tr>
                                <td>{item.idnaUnicode}</td>
                                <td>
                                    {#if item.zone_entry == null}<span>–</span>
                                    {:else}<a use:sanchor href={spath(`/zone/${item.zone_entry.zone_idna}`)}>{item.zone_entry.zone_idna}</a>
                                    {/if}
                                </td>
                                <td>
                                    {#if item.ssl_cert == null}<span>–</span>
                                    {:else}<span><Date value={item.ssl_cert.expiry} format="h-table-s" /></span>
                                    {/if}
                                </td>
                                <td>
                                    {#if item.ssl_cert == null}
                                        <span>
                                            {#if !openAction || openAction.idna != item.idna}
                                                <button
                                                    type="button"
                                                    disabled={working}
                                                    class="btn btn-sm btn-outline-secondary"
                                                    onclick={() => {
                                                        actionItem = item
                                                        openAction = item
                                                    }}>Créer</button
                                                >
                                            {:else}
                                                <button
                                                    type="button"
                                                    disabled={working}
                                                    class="btn btn-sm btn-outline-secondary"
                                                    onclick={() => {
                                                        actionItem = undefined
                                                        openAction = undefined
                                                    }}>Annuler</button
                                                >
                                                <button type="button" disabled={working} class="btn btn-sm btn-primary" onclick={() => confirmCreate()}>Créer</button>
                                            {/if}
                                        </span>
                                    {/if}
                                </td>
                                <td>
                                    {#if item.ssl_cert != null}
                                        <span>
                                            {#if !openAction || openAction.idna != item.idna}
                                                <button
                                                    type="button"
                                                    disabled={working}
                                                    class="btn btn-sm btn-outline-secondary"
                                                    onclick={() => {
                                                        actionItem = item
                                                        openAction = item
                                                    }}>Révoquer</button
                                                >
                                            {:else}
                                                <button
                                                    type="button"
                                                    disabled={working}
                                                    class="btn btn-sm btn-outline-secondary"
                                                    onclick={() => {
                                                        actionItem = undefined
                                                        openAction = undefined
                                                    }}>Annuler</button
                                                >
                                                <button type="button" disabled={working} class="btn btn-sm btn-danger" onclick={() => confirmRevoke()}>Révoquer</button>
                                            {/if}
                                        </span>
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
    </div>
{/if}
